import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
  Collapse,
  IconButton,
} from "@mui/material";
import CopyableTypography from "components/Typography/CopyableTypography";
import { ALL_CA_SECTION_IDS } from "encamp-shared/src/hmbp/sections";
import { ActivityType } from "generated-graphql/graphql";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { useFulfillmentDetails } from "../useFulfillmentDetails";
import { getRelevantPortalCredential } from "../useRelevantPortalCredential";
import { buildGeppettoCommand, getGeppettoAction } from "./geppettoCommandUtils";
import { SectionSelector } from "./SectionSelector";

const GeppettoCommand = () => {
  const theme = useTheme();
  const { data } = useFulfillmentDetails();
  const { activityId } = useParams();
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-geppetto-command-expanded",
    { defaultValue: true }
  );

  const viewingActivity = data?.tierIIReport.currentWorkflow?.activities.find(
    (a) => a.id === activityId
  );

  const isViewingGeppettoActivity =
    viewingActivity?.type === ActivityType.Geppetto ||
    viewingActivity?.type === ActivityType.CollectEvidence;

  const action = getGeppettoAction(
    viewingActivity?.type,
    viewingActivity?.currentJob?.action,
    viewingActivity?.description,
    data?.tierIIReport.reportKind
  );

  const facilityId = data?.tierIIReport.facility.id;

  const reportKind = data?.tierIIReport.reportKind;

  const jobId = viewingActivity?.currentJob?.id;

  const relevantCredential = getRelevantPortalCredential(
    data?.tierIIReport.facility?.portalCredentials,
    viewingActivity
  );
  const credentialId = relevantCredential?.credential?.id;

  const reportId = data?.tierIIReport.id;

  const isCAReport = data?.tierIIReport.facility?.state === "CA";

  const defaultSectionIds = useMemo<string[]>(
    () => (isCAReport ? [...ALL_CA_SECTION_IDS] : []),
    [isCAReport]
  );

  const [sectionIds, setSectionIds] = useState<string[]>(defaultSectionIds);

  // Reset selection only when isCAReport actually transitions, not on every
  // identity flip of `defaultSectionIds` (which can happen during cache
  // invalidations / refetches and would otherwise discard a mid-edit selection).
  const prevIsCAReportRef = useRef(isCAReport);
  useEffect(() => {
    if (prevIsCAReportRef.current !== isCAReport) {
      prevIsCAReportRef.current = isCAReport;
      setSectionIds(defaultSectionIds);
    }
  }, [isCAReport, defaultSectionIds]);

  const allDefaultsSelected =
    sectionIds.length === defaultSectionIds.length &&
    defaultSectionIds.every((id) => sectionIds.includes(id));

  const sectionIdsForCommand = !isCAReport || allDefaultsSelected ? undefined : sectionIds;

  const geppettoCommand = useMemo(() => {
    return buildGeppettoCommand({
      action,
      reportingYear: data?.tierIIReport.reportingYear,
      reportKind,
      facilityId,
      jobId,
      credentialId,
      reportId,
      sectionIds: sectionIdsForCommand,
    });
  }, [
    action,
    facilityId,
    reportKind,
    jobId,
    credentialId,
    reportId,
    data?.tierIIReport.reportingYear,
    sectionIdsForCommand,
  ]);

  if (!isViewingGeppettoActivity) return <></>;

  return (
    <Stack direction={"column"} component={Paper} elevation={0} sx={{ padding: theme.spacing(3) }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="h6">Geppetto Command</Typography>
        <IconButton size="small" sx={{ ml: 1 }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ mt: 3 }}>
          {isCAReport && (
            <>
              <SectionSelector value={sectionIds} onChange={setSectionIds} />
              <Divider sx={{ my: 2 }} />
            </>
          )}
          <CopyableTypography text={geppettoCommand} />
        </Box>
      </Collapse>
    </Stack>
  );
};

export { GeppettoCommand };
